import { useQuery } from '@apollo/client';
import { CheckIcon, PlusIcon, UserIcon, UserPlusIcon } from 'lucide-react';
import { useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Divider,
  P,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTenantName } from '@eluve/smart-blocks';

import { useAddUserToTenant } from '../hooks/useAddUserToTenant';
import { UserFragment } from '../operations';

const GetUsersQuery = graphql(
  `
    query GetUsers($addedUserIds: [uuid!]!) {
      users(where: { id: { _nin: $addedUserIds } }) {
        ...User
      }
    }
  `,
  [UserFragment],
);

interface AddUserActionProps {
  tenantId: string;
  addedUserIds: string[];
}

export const AddUserAction: React.FC<AddUserActionProps> = ({
  tenantId,
  addedUserIds,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const { data, loading } = useQuery(GetUsersQuery, {
    variables: { addedUserIds },
    context: eluveAdminHasuraContext,
  });

  const addUserToTenant = useAddUserToTenant(tenantId);

  const tenantName = useTenantName(tenantId);

  const handleAddUser = () => {
    if (!selectedUserId) return;

    const user = data?.users.find((u) => u.id === selectedUserId);
    const name = user?.displayName ?? `${user?.firstName} ${user?.lastName}`;

    if (!user) return;

    addUserToTenant({
      variables: {
        tenantId,
        userId: selectedUserId,
        role: 'tenant-member',
      },
      onError: () => {
        toast.error(`Failed to add user ${name} to organization ${tenantName}`);
      },
    });

    setOpen(false);
    setSelectedUserId(undefined);
  };

  const handleCancel = () => {
    setOpen(false);
    setSelectedUserId(undefined);
  };

  const hasNoUsersToAdd = data && data.users.length === 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary">
          <UserPlusIcon className="mr-2 size-5" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <div className="flex gap-3">
          <UserPlusIcon className="size-8" />
          <DialogHeader>
            <DialogTitle className="font-normal tracking-normal text-text-secondary">
              Add New user
            </DialogTitle>
            <DialogDescription>
              Organization: <strong>{tenantName}</strong>
            </DialogDescription>
          </DialogHeader>
        </div>

        <Divider />

        {!hasNoUsersToAdd ? (
          <>
            <P>Search user by email or name to add them to the organization.</P>
            <Command className="bg-bg-quaternary/60">
              <CommandInput placeholder="Search user" />
              <Divider className="my-0 border-border-tertiary" />
              <CommandList>
                <CommandEmpty>
                  {loading ? 'Loading...' : 'No users available to add.'}
                </CommandEmpty>
                <CommandGroup
                  heading="Users"
                  className="h-[220px] overflow-auto"
                >
                  {data?.users.map((user) => {
                    const name =
                      user.displayName ?? `${user.firstName} ${user.lastName}`;
                    return (
                      <CommandItem
                        className="text-text-secondary aria-selected:bg-border-secondary aria-selected:text-text-secondary data-[checked]:bg-border-accent-secondary"
                        key={user.id}
                        value={`${name} ${user.email}`}
                        onSelect={() => setSelectedUserId(user.id)}
                        data-checked={
                          selectedUserId === user.id ? 'true' : undefined
                        }
                      >
                        <div className="flex items-center gap-3">
                          {selectedUserId === user.id ? (
                            <CheckIcon className="size-6" />
                          ) : (
                            <UserIcon className="size-6" />
                          )}

                          <P className="whitespace-pre-line font-normal tracking-normal text-text-primary">
                            {name}
                            <br />
                            <span className="text-text-tertiary">
                              {user.email}
                            </span>
                          </P>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>{' '}
          </>
        ) : (
          <P>No users available to add.</P>
        )}
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          {!hasNoUsersToAdd && (
            <Button onClick={handleAddUser} type="button">
              <PlusIcon className="mr-2 size-5" />
              Add
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
